import React, { Fragment, useState, useEffect, useRef } from 'react';
import qs from 'qs';
import { useRefinementList } from 'react-instantsearch';
import RefinementTag from './RefinementTag';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { updateSearchRefFromUrl } from './utils';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { getSession, SessionStorageKeys } from '@jbhi-fi/utils/src/sessionStorage';
import { postAlgoliaFilterClick, postAlgoliaFilterView } from '../../../utils/algolia/search-insights';

interface CheckboxRefinementProps {
  facet: string;
  limit: number;
  showMoreLimit: number;
  setDisableScroll: any;
  trackingInfo: any;
  onDropdownToggle?: (isOpen: boolean) => void;
}
/**
 * Function for to create checkboxes based on the items and facets
 */
const makeCheckboxItems = (items, facet, filterSearchTerm, isSomeRefined) => {
  let checkboxItems = filterSearchTerm
    ? items.filter(s => s.label.toLowerCase().startsWith(filterSearchTerm.toLowerCase()))
    : items;
  if (facet === 'Brand') {
    checkboxItems = checkboxItems.sort((a, b) => a.label.localeCompare(b.label));
  } else {
    checkboxItems = checkboxItems.sort((a, b) => b.count - a.count);
  }

  // Include refined items when not expanded and sort again
  if (isSomeRefined) {
    if (facet === 'Brand') {
      checkboxItems = [...new Set([...checkboxItems, ...items.filter(f => f.isRefined === true)])].sort((a, b) =>
        a.label.localeCompare(b.label)
      );
    } else {
      checkboxItems = [...new Set([...checkboxItems, ...items.filter(f => f.isRefined === true)])].sort(
        (a, b) => b.count - a.count
      );
    }
  }

  return checkboxItems;
};

export const CheckboxRefinement = ({
  facet,
  limit,
  showMoreLimit,
  setDisableScroll,
  trackingInfo,
  onDropdownToggle,
}: CheckboxRefinementProps) => {
  const attribute = `facets.${facet}`;
  const { items, refine } = useRefinementList({ attribute, showMoreLimit, limit });
  const isSomeRefined = items.filter(f => f.isRefined === true).length;
  const [showCheckboxInput, setShowCheckboxInput] = useState(false);
  const RefineButton = useRef<HTMLButtonElement>(null);
  const RefineDropdown = useRef<HTMLDivElement>(null);
  const [checkedItems, setCheckedItems] = useState(items.filter(i => i.isRefined));
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
  const searchState = qs.parse(window.location.search.slice(1)) as any;
  const searchRef = useRef(searchState);
  const [clickType, setClickType] = useState('');

  const checkboxItems = makeCheckboxItems(items, facet, filterSearchTerm, isSomeRefined);

  let prependedLabel = '';

  useEffect(() => {
    // Add Inches to screen size facet
    if (facet === 'Screen Size (Inches)') {
      prependedLabel = '"';
    }
    return () => {
      document.body.removeEventListener('pointerdown', externalClick);
    };
  }, []);

  useEffect(() => {
    const updatedItems = makeCheckboxItems(items, facet, filterSearchTerm, isSomeRefined);
    if (checkedItems.length === 0 && updatedItems.filter(i => i.isRefined).length > 0) {
      setCheckedItems(updatedItems.filter(i => i.isRefined));
    } else if (updatedItems.filter(i => i.isRefined).length === 0) {
      setCheckedItems([]);
    }
  }, [items, filterSearchTerm]);

  /**
   * Function to handle dropdown toggle (clicking button)
   */
  const toggleDropdown = event => {
    onDropdownToggle?.(!showCheckboxInput);
    pushToDataLayer({
      event: 'open_filter_group',
      filter_group: facet,
      search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
      search_term: searchState?.query || '',
    });
    postAlgoliaFilterView(facet);
    event.preventDefault();
    setFilterSearchTerm('');
    setShowCheckboxInput(!showCheckboxInput);
  };

  /**
   * Function to handle external clicks
   */
  const externalClick = event => {
    const Button = RefineButton.current;
    const Dropdown = RefineDropdown.current;

    // Close price range dropdown if clicked outside of target window (desktop)
    if (!event || (Dropdown && !Dropdown.contains(event.target) && Button && !Button.contains(event.target))) {
      document.body.removeEventListener('pointerdown', externalClick);
      setShowCheckboxInput(false);
      setDisableScroll(false);
    }
  };

  useEffect(() => {
    if (showCheckboxInput) {
      document.body.addEventListener('pointerdown', externalClick);
      setDisableScroll(true);
    } else {
      document.body.removeEventListener('pointerdown', externalClick);
      setDisableScroll(false);
    }
    return () => {
      document.body.removeEventListener('pointerdown', externalClick);
    };
  }, [showCheckboxInput]);

  const toggleSelect = e => {
    const filters = Object.keys(searchState)
      .filter(key => !['query', 'page', 'hitsPerPage', 'sortBy'].includes(key))
      .reduce((obj, key) => {
        if (key.toLowerCase() === 'price') {
          obj[key.toLowerCase()] = { type: 'range', value: searchState[key] };
        } else {
          obj[key.toLowerCase()] = Array.isArray(searchState[key])
            ? searchState[key].map(i => ({ type: 'checkbox', value: i }))
            : { type: 'checkbox', value: searchState[key] };
        }
        return obj;
      }, {});

    updateSearchRefFromUrl(searchRef);

    if (isSomeRefined) {
      const checkedItems = checkboxItems.filter(i => i.isRefined);
      checkedItems.forEach(i => {
        refine(i.value);
      });

      pushToDataLayer({
        event: 'clear_filter_group',
        filter_group: facet,
        search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
        search_term: searchState?.query || '',
      });
      window.dataLayer.push({
        event: 'custom.filter.set',
        group: 'site search',
        category: 'search',
        label: 'Clear all',
        name: facet,
        value: 'On',
        info: {
          element: e.target.name,
          position: trackingInfo.position,
          sortyBy: searchRef.current.sortBy || trackingInfo.defaults.sortBy,
          hitsPerPage: Number(searchRef.current.hitsPerPage) || trackingInfo.defaults.hitsPerPage,
        },
        newFilters: {
          ...filters,
          [facet.toLowerCase()]: [],
        },
        oldFilters: filters,
      });
    } else {
      checkboxItems.forEach(i => {
        refine(i.value);
      });

      window.dataLayer.push({
        event: 'custom.filter.set',
        group: 'site search',
        category: 'search',
        label: 'Select all',
        name: facet,
        value: 'On',
        info: {
          element: e.target.name,
          position: trackingInfo.position,
          sortyBy: searchRef.current.sortBy || trackingInfo.defaults.sortBy,
          hitsPerPage: Number(searchRef.current.hitsPerPage) || trackingInfo.defaults.hitsPerPage,
        },
        newFilters: {
          ...filters,
          [facet.toLowerCase()]: checkboxItems.map(i => ({ type: 'checkbox', value: i.value[0] })),
        },
        oldFilters: filters,
      });
    }
  };

  const labelOnClickHandler = () => {
    setClickType('text');
  };

  const toggleCheck = (e, item) => {
    setClickType('checkbox');
    pushToDataLayer({
      event: 'select_filter',
      filter_group: facet,
      filter_name: 'undefined',
      filter_type: clickType,
      filter_value: item.label,
      search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
      search_term: searchState?.query || '',
    });
    postAlgoliaFilterClick(facet, item.label);
    e.persist();
    let values = '';

    const filters = Object.keys(searchState)
      .filter(key => !['query', 'page', 'hitsPerPage', 'sortBy'].includes(key))
      .reduce((obj: any, key: string) => {
        if (key.toLowerCase() === 'price') {
          obj[key.toLowerCase()] = { type: 'range', value: searchState[key] };
        } else {
          obj[key.toLowerCase()] = Array.isArray(searchState[key])
            ? (searchState[key] as Array<string>).map(i => ({ type: 'checkbox', value: i }))
            : { type: 'checkbox', value: searchState[key] };
        }
        return obj;
      }, {});

    if (item.isRefined) {
      setCheckedItems([...checkedItems.filter(i => i.label !== item.label)]);
      pushToDataLayer({
        event: 'unselect_filter',
        filter_group: facet,
        filter_name: 'undefined',
        filter_type: clickType,
        filter_value: item.label,
        search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
        search_term: searchState?.query || '',
      });
    } else {
      setCheckedItems([...checkedItems.filter(i => i !== item), { ...item, isRefined: true }]);
    }

    if (Array.isArray(item.value) && item.value.length > 1) {
      if (item.isRefined && item.value.filter(i => i === item.label || i[0] === item.label).length > 0) {
        values = item.value.filter(i => i !== item.label && i[0] !== item.label);
      } else {
        values = item.value;
      }
    } else {
      values = item.value;
    }

    refine(values);
    updateSearchRefFromUrl(searchRef);

    window.dataLayer.push({
      event: 'custom.filter.set',
      group: 'site search',
      category: 'search',
      label: item.isRefined ? 'off' : 'on',
      name: facet,
      value: item.label,
      info: {
        element: e.target.name,
        position: trackingInfo.position,
        sortyBy: searchRef.current.sortBy || trackingInfo.defaults.sortBy,
        hitsPerPage: Number(searchRef.current.hitsPerPage) || trackingInfo.defaults.hitsPerPage,
      },
      newFilters: {
        ...filters,
        [facet.toLowerCase()]: values ? { type: 'checkbox', value: values } : undefined,
      },
      oldFilters: filters,
    });
  };

  if (!filterSearchTerm && checkboxItems.length <= 0) {
    return <></>;
  }

  const handleChange = event => {
    const { value } = event.target;
    setFilterSearchTerm(value);
  };

  if (items.length <= 1) return null;

  return (
    <div className="search-results-facet">
      <button
        type="button"
        className={`search-results-facet-button${showCheckboxInput ? ' active' : ''}${
          isSomeRefined > 0 ? ' refined' : ''
        }`}
        ref={RefineButton}
        onClick={toggleDropdown.bind(this)}
      >
        {facet}
        {isSomeRefined > 0 && ' (' + isSomeRefined + ')'}
      </button>
      <div
        className={`search-results-facet-dropdown ais-RefinementList ${showCheckboxInput ? 'active' : ''}`}
        ref={RefineDropdown}
      >
        {checkedItems.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {checkedItems.map((item, index) => {
              if (item.isRefined) {
                return (
                  <RefinementTag
                    key={index}
                    label={item.label}
                    handleTagRemoval={e =>
                      toggleCheck(
                        e,
                        checkboxItems.find(i => i.label === item.label)
                      )
                    }
                  />
                );
              }
            })}
          </div>
        )}
        {items.length >= 10 && (
          <input
            type="text"
            className={'filter-search'}
            value={filterSearchTerm}
            onChange={handleChange.bind(this)}
            placeholder={`Search`}
            autoComplete="true"
            autoCapitalize="none"
            style={!isSomeRefined ? { marginTop: '0.9375rem' } : null}
            autoFocus
          />
        )}
        <ul className="ais-RefinementList-list">
          {checkboxItems.length === 0 && <li style={{ padding: '20px', textAlign: 'center' }}>Nothing to refine!</li>}
          {checkboxItems.map((item, index) => (
            <li key={index} className="ais-RefinementList-item">
              <label className="ais-RefinementList-label">
                <input
                  className="ais-RefinementList-checkbox"
                  type="checkbox"
                  name={item.label}
                  value={item.value}
                  checked={checkedItems.some(i => i.label === item.label && i.isRefined)}
                  onChange={e => toggleCheck(e, item)}
                />
                <span onClick={labelOnClickHandler} className="ais-RefinementList-labelText">
                  <Fragment>{item.label + prependedLabel}</Fragment>
                </span>
                <span className="ais-RefinementList-count">
                  <Fragment>{item.count}</Fragment>
                </span>
              </label>
            </li>
          ))}
        </ul>
        <Button classes="ais-ShowResults" type="primary" onClick={toggleDropdown.bind(this)} label="Show results" />
      </div>
    </div>
  );
};
